import { useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { NAVIGATION, type NavGroup } from '@/config/navigation';
import { usePermissions } from '@/auth/permissions';

interface SidebarNavProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

/** Renders the permission-filtered, grouped navigation (spec §4). */
export function SidebarNav({ collapsed, onNavigate }: SidebarNavProps) {
  const { can } = usePermissions();

  const groups = useMemo<NavGroup[]>(
    () =>
      NAVIGATION.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => !item.permission || can(item.permission, item.permissionMode ?? 'all'),
        ),
      })).filter((group) => group.items.length > 0),
    [can],
  );

  return (
    <Box component="nav" aria-label="Main navigation" sx={{ px: collapsed ? 0.5 : 1, py: 1 }}>
      {groups.map((group) => (
        <List
          key={group.label}
          dense
          subheader={
            collapsed ? undefined : (
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  fontSize: 11,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  lineHeight: 2.4,
                }}
              >
                {group.label}
              </ListSubheader>
            )
          }
        >
          {group.items.map((item) => {
            const Icon = item.icon;
            const button = (
              <ListItemButton
                key={item.path}
                component={NavLink}
                to={item.path}
                onClick={onNavigate}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.25,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&.active': {
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            );

            return collapsed ? (
              <Tooltip key={item.path} title={item.label} placement="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}
        </List>
      ))}
    </Box>
  );
}
